// src/components/Assets/assetsApi.js
// API service functions for Assets module

import axios from "axios";

const BASE_URL = "/api";

// ============================================
// ASSETS CRUD OPERATIONS
// ============================================

/**
 * Fetch all assets belonging to a tenant
 * @param {number} tenantId - Tenant ID
 * @param {string} token - Authorization token
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<Array>} List of assets
 */
export const fetchTenantAssets = async (tenantId, token, signal) => {
  const response = await axios.get(
    `${BASE_URL}/asset/findByTenantId/${tenantId}`,
    {
      headers: { Authorization: `${token}` },
      signal
    }
  );
  return response.data.items || [];
};

/**
 * Create a new asset
 * @param {object} asset - Asset data
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Created asset response
 */
export const createAsset = async (asset, token) => {
  const response = await axios.post(
    `${BASE_URL}/asset`,
    asset,
    {
      headers: { Authorization: `${token}` }
    }
  );
  return response.data;
};

/**
 * Update an existing asset
 * @param {object} asset - Asset data with ID
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Updated asset response
 */
export const updateAsset = async (asset, token) => {
  const response = await axios.put(
    `${BASE_URL}/asset`,
    asset,
    {
      headers: { Authorization: `${token}` }
    }
  );
  return response.data;
};

/**
 * Delete an asset
 * @param {number} assetId - Asset ID to delete
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Deletion response
 */
export const deleteAsset = async (assetId, token) => {
  const response = await axios.delete(
    `${BASE_URL}/asset/${assetId}`,
    {
      headers: { Authorization: `${token}` }
    }
  );
  return response.data;
};

// ============================================
// ASSET PERMISSIONS OPERATIONS
// ============================================

/**
 * Fetch pending permission requests for a tenant (Admin view)
 * @param {number} tenantId - Tenant ID
 * @param {string} token - Authorization token
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<Array>} List of pending permissions
 */
export const fetchTenantPendingPermissions = async (tenantId, token, signal) => {
  const response = await axios.get(
    `${BASE_URL}/assetPermissions/getPermissionsByTenantId/${tenantId}`,
    {
      headers: { Authorization: `${token}` },
      signal
    }
  );
  return response.data.items || [];
};

/**
 * Fetch permission requests for a specific farm (Farmer view)
 * @param {number} farmId - Farm ID
 * @param {string} token - Authorization token
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<Array>} List of farm's permission requests
 */
export const fetchFarmPermissions = async (farmId, token, signal) => {
  const response = await axios.get(
    `${BASE_URL}/assetPermissions/getPermissionsByFarmId/${farmId}`,
    {
      headers: { Authorization: `${token}` },
      signal
    }
  );
  return response.data.items || [];
};

/**
 * Update permission request status (approve/deny)
 * @param {number} permissionId - Permission request ID
 * @param {string} status - New status ("approved" or "denied")
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Update response
 */
export const updatePermissionStatus = async (permissionId, status, token) => {
  const response = await axios.put(
    `${BASE_URL}/assetPermissions/${permissionId}`,
    {},
    {
      headers: { Authorization: `${token}` },
      params: { status }
    }
  );
  return response.data;
};

/**
 * Submit a new asset permission request
 * @param {object} permission - Permission request data
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Created permission response
 */
export const requestAssetPermission = async (permission, token) => {
  const response = await axios.post(
    `${BASE_URL}/assetPermissions`,
    permission,
    {
      headers: { Authorization: `${token}` }
    }
  );
  return response.data;
};

/**
 * Fetch permitted assets for a farm
 * @param {number} farmId - Farm ID
 * @param {string} token - Authorization token
 * @returns {Promise<Array>} List of permitted assets
 */
export const fetchPermittedAssets = async (farmId, token) => {
  const response = await axios.get(
    `${BASE_URL}/asset/getPermittedAssets/${farmId}`,
    {
      headers: { Authorization: `${token}` }
    }
  );
  return response.data.items || [];
};

// ============================================
// ASSET EXPENSES OPERATIONS
// ============================================

/**
 * Fetch all expenses for assets
 * @param {number} tenantId - Tenant ID
 * @param {number} farmId - Farm ID (optional)
 * @param {string} token - Authorization token
 * @param {AbortSignal} signal - Optional abort signal for cancellation
 * @returns {Promise<Array>} List of asset expenses
 */
export const fetchAssetExpenses = async (tenantId, farmId, token, signal) => {
  const params = {};
  if (farmId) {
    params.farmId = farmId;
  }

  const response = await axios.get(
    `${BASE_URL}/payment/findByTenantId/${tenantId}`,
    {
      headers: { Authorization: `${token}` },
      params,
      signal
    }
  );
  return response.data.items || [];
};

/**
 * Create a new asset expense
 * @param {object} expense - Expense transaction data
 * @param {number} assetId - Asset ID
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Created expense response
 */
export const createAssetExpense = async (expense, token) => {
  const response = await axios.post(
    `${BASE_URL}/payment`,
    expense,  // assetId should already be in the expense object
    {
      headers: { Authorization: `${token}` }
    }
  );
  return response.data;
};
